/*
 * Copyright (c) 2024. Bolmate B.V.
 */
import './style/userSelection.scss';

import {AwesomeIcon, ChangeFunction, Checkbox, Loader, SelectItemShape} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import React, {MouseEventHandler, useState} from 'react';
import {SelectionUser} from "../../../util/interfaces";
import {BolmateButton, BolmateSelect, DvrdDatePicker} from "../../controls";
import {DUTCH_DATE_FORMAT} from "../../../util/constants";
import classNames from 'classnames';
import CommonPagination from "../../misc/commonPagination";

interface Props {
    onChangeCreatedBefore: ChangeFunction<IDate>;
    onClearCreatedBefore: MouseEventHandler;
    onChangeCreatedAfter: ChangeFunction<IDate>;
    onClearCreatedAfter: MouseEventHandler;
    onChangeSubStatus: ChangeFunction<'active' | 'inactive' | ''>;
    onChangeIncludeTrials: ChangeFunction<boolean>;
    onChangeIncludeFree: ChangeFunction<boolean>;
    onChangeIncludeUnpaid: ChangeFunction<boolean>;
    onChangePage: (page: number) => MouseEventHandler;
    onChangeLimit: ChangeFunction<number>;
    onApplyFilters: MouseEventHandler;
    onExportUsers: MouseEventHandler;
    onCopyData: (value: string) => MouseEventHandler;
    createdBefore: IDate | null;
    createdAfter: IDate | null;
    subStatus: 'active' | 'inactive' | null;
    includeTrials: boolean;
    includeFree: boolean;
    includeUnpaid: boolean;
    loading: boolean;
    users: Array<SelectionUser>;
    total: number;
    page: number;
    limit: number;
}

const subStatusItems: Array<SelectItemShape> = [
    {value: '', label: 'Alle'},
    {value: 'active', label: 'Actief'},
    {value: 'inactive', label: 'Inactief'},
]

const limitItems: Array<SelectItemShape> = [
    {value: -1, label: 'Alle'},
    {value: 20, label: 20},
    {value: 50, label: 50},
    {value: 100, label: 100}
]

export default function UserSelection(props: Props) {
    const {
        onChangeCreatedAfter, onChangeCreatedBefore, onClearCreatedAfter, onClearCreatedBefore, createdAfter,
        createdBefore, limit, onChangeLimit, onChangeIncludeFree, onChangeIncludeTrials, includeTrials, onChangePage,
        loading, onApplyFilters, onExportUsers, users, onChangeSubStatus, subStatus, page, total, includeFree,
        onCopyData, onChangeIncludeUnpaid, includeUnpaid
    } = props;
    const [filtersOpen, setFiltersOpen] = useState(true);

    function onToggleFilters() {
        setFiltersOpen(!filtersOpen);
    }

    function renderFilters() {
        const _includeTrials = subStatus === 'active' && includeTrials;
        const _includeFree = subStatus === 'active' && includeFree;
        const _includeUnpaid = subStatus === 'active' && includeUnpaid;
        return (
            <div className={classNames('user-selection-filters', filtersOpen && 'open')}>
                <div className='filters-head' onClick={onToggleFilters}>
                    <label className='filters-title'>Filters</label>
                    <div className='line'/>
                    <AwesomeIcon name='chevron-down' className='filters-chev'/>
                </div>
                <div className='filters-container'>
                    <div className='filter-group'>
                        <label className='group-label'>Registratiedatum</label>
                        <div className='date-field'>
                            <DvrdDatePicker onChange={onChangeCreatedBefore} value={createdBefore}
                                            label='Account aangemaakt voor' placeholder='dd-mm-jjjj'
                                            dateFormat={DUTCH_DATE_FORMAT} closeOnChange/>
                            <AwesomeIcon name='times' className='clear-date' onClick={onClearCreatedBefore}/>
                        </div>
                        <div className='date-field'>
                            <DvrdDatePicker onChange={onChangeCreatedAfter} value={createdAfter}
                                            label='Account aangemaakt na' placeholder='dd-mm-jjjj'
                                            dateFormat={DUTCH_DATE_FORMAT} max={IDate.now()} closeOnChange/>
                            <AwesomeIcon name='times' className='clear-date' onClick={onClearCreatedAfter}/>
                        </div>
                    </div>
                    <div className='filter-group'>
                        <label className='group-label'>Abonnement</label>
                        <BolmateSelect onChange={onChangeSubStatus} value={subStatus ?? ''} items={subStatusItems}
                                       label='Status' className='sub-status-select'/>
                        <div className='check-group'>
                            <Checkbox checked={_includeTrials} onCheck={onChangeIncludeTrials} label='Inclusief proef'
                                      disabled={subStatus !== 'active'}/>
                            <Checkbox checked={_includeFree} onCheck={onChangeIncludeFree} label='Inclusief gratis'
                                      disabled={subStatus !== 'active'}/>
                            <Checkbox checked={_includeUnpaid} onCheck={onChangeIncludeUnpaid}
                                      label='Inclusief onbetaald' disabled={subStatus !== 'active'}/>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    function renderActions() {
        return (
            <div className='user-selection-actions'>
                <BolmateButton onClick={onApplyFilters} label='Gebruikers selecteren'/>
                <BolmateButton onClick={onExportUsers} label='Gebruikers exporteren'/>
            </div>
        )
    }

    function renderUsers() {
        return (
            <div className='selections-users-container'>
                <div className='users-table'>
                    <div className='default-table-row row head'>
                        <label>ID</label>
                        <label>Naam</label>
                        <label>E-mailadres</label>
                        <label>Abonnement gestart</label>
                        <label>Abonnement status</label>
                        <label>Heeft betaalwijze</label>
                    </div>
                    {users.map(renderUser)}
                    {!users.length && <div className='default-table-row empty'>
                        <label className='empty-label'>- Geen gebruikers geselecteerd -</label>
                    </div>}
                </div>
                <CommonPagination onChangePage={onChangePage} onChangeLimit={onChangeLimit} page={page} total={total}
                                  limit={limit} limitItems={limitItems}/>
            </div>
        )
    }

    function renderUser(user: SelectionUser, index: number) {
        const fullNameParts: Array<string> = [];
        if (user.firstname.length) fullNameParts.push(user.firstname);
        if (user.lastname.length) fullNameParts.push(user.lastname);
        const fullName = fullNameParts.join(' ');
        return (
            <div key={index} className='default-table-row row'>
                <label className='click' onClick={onCopyData(user.id)}>{user.id}</label>
                <label className='click' onClick={onCopyData(fullName)}>{fullName}</label>
                <label className='click' onClick={onCopyData(user.email)}>{user.email}</label>
                <label>{new IDate(user.subscription_created_at).format(DUTCH_DATE_FORMAT)}</label>
                <label>{user.subscription_status}</label>
                <label>{user.has_payment_method ? 'Ja' : 'Nee'}</label>
            </div>
        )
    }

    return (
        <div className='user-selection'>
            <Loader active={loading}/>
            {renderFilters()}
            {renderActions()}
            {renderUsers()}
        </div>
    )
}